import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TOTAL_TRIAL_DAYS = Number(process.env.REACT_APP_TOTAL_TRIAL_DAYS || 30);

const SubscriptionTrialStatus = () => {
  const [daysLeft, setDaysLeft] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchTrialStatus = async () => {
      try {
        const customerId = localStorage.getItem("customerId");
        if (!customerId) return;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
          {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        );

        const customer = res.data;

        if (customer?.PaymentRequired || customer?.IsActive === "Offboard") {
          setDaysLeft(null);
          return;
        }

        if (customer?.Status !== "Trial") {
          setDaysLeft(null);
          return;
        }

        if (!customer?.NextBillingDate) return;

        const today = new Date();
        const end = new Date(customer.NextBillingDate);

        const diffDays = Math.ceil(
          (end - today) / (1000 * 60 * 60 * 24)
        );

        setDaysLeft(diffDays);

      } catch (err) {
        console.error(err);
      }
    };

    fetchTrialStatus();
  }, []);

  if (daysLeft === null) return null;

  const safeDays = Math.max(daysLeft, 0);

  const isExpired = safeDays === 0;

  const progress = safeDays / TOTAL_TRIAL_DAYS;

  // -------------------
  // RING SETUP
  // -------------------
  const radius = 18; // was 14
  const circumference = 2 * Math.PI * radius;

  // 0 offset = full ring
  // full circumference = empty ring
  const offset = circumference * (1 - progress);

  // -------------------
  // UI TEXT
  // -------------------
  const statusText = isExpired
    ? t("statusExpired")
    : t("statusActive");

  const message = isExpired
    ? t("upgradePlan")
    : t("trialDaysRemaining", { count: safeDays });

  const ringColor = isExpired
    ? "text-danger"
    : "text-primary";

  const statusColor = isExpired
    ? "text-danger"
    : "badge-subtle-success";

  return (
    <div
      onClick={() => navigate("/my-subscription")}
      className={`d-inline-flex align-items-center rounded-pill bg-primary-subtle shadow-none pointer ${isExpired ? "px-3 py-2" : "px-2"}`}
    >
      {/* TEXT */}
      <div className={isExpired ? "text-center" : "ps-2"}>
        <div className="fs-11">
          <span>{t("Trial")} • </span>

          <span className={`${statusColor} bg-transparent`}>
            {statusText}
          </span>
        </div>

        <div className="text-primary fs-11">
          {message}
        </div>
      </div>

      {/* PROGRESS CIRCLE */}
      {!isExpired && (
        <svg width="44" height="44">
        {/* White center */}
        <circle cx="22" cy="22" r={radius - 2} fill="white" />

        {/* Background ring */}
        <circle
          cx="22"
          cy="22"
          r={radius}
          fill="none"
          stroke="#d6d6d6"
          strokeWidth="2"
        />

        {/* Progress ring */}
        <circle
          cx="22"
          cy="22"
          r={radius}
          fill="none"
          stroke="currentColor"
          className={ringColor}
          strokeWidth="2"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 22 22)"
        />
        {/* Number */}
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill="currentColor"
          className={`fw-bold text-primary fs-10`}
        >
          {safeDays}
        </text>
      </svg>
      )}
    </div>
  );
};

export default SubscriptionTrialStatus;